import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, errorMessage } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { Copy, Crown, Send, UserPlus, AlertTriangle, Lock, LogOut, Check, X, Trash2, FileText, ArrowRight, Trophy, Medal, Award, Download, Linkedin, Twitter } from "lucide-react";
import InvoiceBar from "../components/InvoiceBar";
import { API_BASE } from "../lib/api";

const formatDate = (iso) => { try { return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return "—"; } };
const formatDay = (iso) => { try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); } catch { return "—"; } };

export default function TeamDashboard() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/team/my");
      if (!data.team) {
        navigate("/team", { replace: true });
        return;
      }
      setData(data);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  if (!data) return (<div className="App"><Navbar /><div className="min-h-[60vh] flex items-center justify-center"><span className="spinner" /></div></div>);

  const { team, is_leader, members, join_requests, pending_invitations, member_count, min_team_size, max_team_size, team_lock_deadline, lock_passed } = data;
  const changesLocked = data.changes_locked ?? lock_passed;
  const isFull = member_count >= max_team_size;
  const belowMin = member_count < min_team_size;

  const copyId = () => { navigator.clipboard.writeText(team.id); toast.success("Team ID copied"); };

  const sendInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.includes("@")) return toast.error("Enter a valid college email");
    setBusy(true);
    try {
      await api.post("/team/invite", { college_email: inviteEmail });
      toast.success("Invitation sent");
      setInviteEmail("");
      await load();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  const acceptReq = async (user_id) => {
    setBusy(true);
    try { await api.post("/team/accept-request", { user_id }); toast.success("Member added"); await load(); }
    catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };
  const rejectReq = async (user_id) => {
    setBusy(true);
    try { await api.post("/team/reject-request", { user_id }); toast.success("Request rejected"); await load(); }
    catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };
  const removeMember = async (user_id) => {
    if (!window.confirm("Remove this member from the team?")) return;
    setBusy(true);
    try { await api.post("/team/remove-member", { user_id }); toast.success("Member removed"); await load(); }
    catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };
  const leaveTeam = async () => {
    if (!window.confirm("Are you sure you want to leave this team?")) return;
    setBusy(true);
    try {
      await api.post("/team/leave");
      toast.success("You left the team");
      await refresh();
      navigate("/team");
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };
  const disbandTeam = async () => {
    if (!window.confirm("Disband this team? This permanently deletes the team and notifies all members by email. This cannot be undone.")) return;
    setBusy(true);
    try {
      await api.post("/team/disband");
      toast.success("Team disbanded — members have been notified");
      await refresh();
      navigate("/team");
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  return (
    <div className="App" data-testid="team-dashboard-page">
      <Navbar />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        {/* HEADER */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="label-eyebrow mb-2">Your Team</div>
            <div className="flex items-center gap-3">
              <h1 className="font-display text-4xl sm:text-5xl text-white" data-testid="team-name">{team.name}</h1>
              <span className={`badge badge-${team.status}`} data-testid="team-status">{statusLabel(team.status)}</span>
            </div>
            <div className="mt-3 flex items-center gap-2 text-sm">
              <span className="label-eyebrow">Team ID</span>
              <code className="font-mono text-gray-400 text-xs">{team.id}</code>
              <button onClick={copyId} className="text-gray-500 hover:text-brand" data-testid="copy-team-id"><Copy className="w-3.5 h-3.5" /></button>
            </div>
          </div>
          <div className="text-right">
            <div className="label-eyebrow">Members</div>
            <div className="font-display text-4xl text-white"><span data-testid="member-count">{member_count}</span><span className="text-gray-500 text-2xl"> / {max_team_size}</span></div>
          </div>
        </div>

        {/* BANNERS */}
        {changesLocked && (
          <div className="mt-6 p-4 border border-[#EF4444]/40 bg-[#EF4444]/5 flex items-center gap-3" data-testid="lock-banner">
            <Lock className="w-5 h-5 text-[#EF4444]" strokeWidth={1.5} />
            <div className="text-sm text-gray-300">{data.results_announced ? "Team changes are locked — results have been published." : "Team changes are locked. The team lock deadline has passed."}</div>
          </div>
        )}
        {!changesLocked && belowMin && (
          <div className="mt-6 p-4 border border-[#F59E0B]/40 bg-[#F59E0B]/5 flex items-start gap-3" data-testid="warning-below-min">
            <AlertTriangle className="w-5 h-5 text-[#F59E0B] mt-0.5" strokeWidth={1.5} />
            <div className="text-sm text-gray-300">
              <div className="font-display text-white text-base mb-1">Add at least {min_team_size - member_count} more member{min_team_size - member_count === 1 ? "" : "s"}</div>
              Your team is below the minimum size of {min_team_size}. Teams below the minimum will be auto-disqualified after the lock deadline.
            </div>
          </div>
        )}
        {!changesLocked && (
          <div className="mt-3 text-xs label-eyebrow text-gray-500" data-testid="lock-info">
            Team changes locked after <span className="text-brand">{formatDate(team_lock_deadline)}</span>
          </div>
        )}

        {/* RESULT BANNER (winner / finalist / participant) */}
        <ResultBanner team={team} />

        {/* SUBMISSION STATUS CARD */}
        <SubmissionStatusCard teamStatus={team.status} isLeader={is_leader} />

        {/* INVOICE / RECEIPT BAR */}
        <InvoiceBar user={user} />

        {/* GRID */}
        <div className="mt-10 grid lg:grid-cols-3 gap-6">
          {/* Members list */}
          <div className="lg:col-span-2">
            <div className="card-surface">
              <div className="flex items-center justify-between mb-4">
                <div className="label-eyebrow">Active Members</div>
                <div className="font-mono text-xs text-gray-500">{member_count} accepted</div>
              </div>
              <div className="space-y-2" data-testid="members-list">
                {members.map((m) => (
                  <div key={m.id} className="border border-white/5 p-3 flex items-center justify-between" data-testid={`member-${m.user_id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-brand/10 border border-brand/30 flex items-center justify-center font-display text-brand">
                        {m.name?.[0]?.toUpperCase() || "?"}
                      </div>
                      <div>
                        <div className="text-white flex items-center gap-2">
                          {m.name}
                          {m.user_id === team.leader_id && <Crown className="w-3.5 h-3.5 text-brand" strokeWidth={1.5} />}
                        </div>
                        <div className="text-xs text-gray-500">{m.college_name} · {m.college_email}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="badge badge-accepted">accepted</span>
                      {is_leader && m.user_id !== team.leader_id && !changesLocked && (
                        <button onClick={() => removeMember(m.user_id)} disabled={busy} className="text-gray-500 hover:text-[#EF4444] p-1.5" data-testid={`remove-member-${m.user_id}`} title="Remove member">
                          <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* leave button for non-leaders */}
              {!is_leader && !changesLocked && (
                <button onClick={leaveTeam} disabled={busy} className="btn-secondary mt-6 flex items-center gap-2 text-[#EF4444] border-[#EF4444]/30 hover:!border-[#EF4444]/60" data-testid="leave-team-btn">
                  <LogOut className="w-4 h-4" strokeWidth={1.5} /> Leave Team
                </button>
              )}
              {/* disband button for the leader */}
              {is_leader && !changesLocked && (
                <button onClick={disbandTeam} disabled={busy} className="btn-secondary mt-6 flex items-center gap-2 text-[#EF4444] border-[#EF4444]/30 hover:!border-[#EF4444]/60" data-testid="disband-team-btn">
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} /> Disband Team
                </button>
              )}
            </div>
          </div>

          {/* Sidebar: invites & requests */}
          <div className="space-y-6">
            {is_leader && !isFull && !changesLocked && (
              <div className="card-surface" data-testid="invite-section">
                <div className="label-eyebrow mb-3">Invite Member</div>
                <form onSubmit={sendInvite} className="space-y-3">
                  <input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="teammate@university.ac.in" className="input-field" data-testid="invite-email-input" />
                  <button type="submit" disabled={busy} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="invite-send-btn">
                    {busy ? <span className="spinner" /> : <Send className="w-4 h-4" strokeWidth={1.5} />} Send Invitation
                  </button>
                </form>
                {pending_invitations?.length > 0 && (
                  <div className="mt-5">
                    <div className="label-eyebrow mb-2">Pending Invitations</div>
                    <div className="space-y-2">
                      {pending_invitations.map((inv) => (
                        <div key={inv.id} className="border border-white/5 p-2.5 text-xs flex items-center justify-between" data-testid={`pending-invitation-${inv.id}`}>
                          <span className="text-gray-400 truncate">{inv.invited_email}</span>
                          <span className="badge badge-invited">pending</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {is_leader && (
              <div className="card-surface" data-testid="join-requests-section">
                <div className="label-eyebrow mb-3 flex items-center gap-2"><UserPlus className="w-3.5 h-3.5" /> Join Requests</div>
                {join_requests.length === 0 ? (
                  <div className="text-sm text-gray-500">No pending requests.</div>
                ) : (
                  <div className="space-y-2">
                    {join_requests.map((r) => (
                      <div key={r.id} className="border border-white/5 p-3" data-testid={`join-request-${r.user_id}`}>
                        <div className="text-sm text-white">{r.name}</div>
                        <div className="text-xs text-gray-500">{r.college_name}</div>
                        {!changesLocked && (
                          <div className="flex gap-2 mt-3">
                            <button onClick={() => acceptReq(r.user_id)} disabled={busy || isFull} className="flex-1 bg-[#10B981]/10 border border-[#10B981]/30 text-[#10B981] py-1.5 text-xs uppercase tracking-widest font-mono hover:bg-[#10B981]/20 flex items-center justify-center gap-1" data-testid={`accept-${r.user_id}`}>
                              <Check className="w-3.5 h-3.5" /> Accept
                            </button>
                            <button onClick={() => rejectReq(r.user_id)} disabled={busy} className="flex-1 bg-[#EF4444]/10 border border-[#EF4444]/30 text-[#EF4444] py-1.5 text-xs uppercase tracking-widest font-mono hover:bg-[#EF4444]/20 flex items-center justify-center gap-1" data-testid={`reject-${r.user_id}`}>
                              <X className="w-3.5 h-3.5" /> Reject
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

const statusLabel = (s) => {
  const map = {
    shortlisted: "Shortlisted · Round 2",
    finalist: "Finalist",
    registered: "Registered",
    forming: "Forming",
    disqualified: "Disqualified",
  };
  return map[s] || s;
};

function SubmissionStatusCard({ teamStatus, isLeader }) {
  const { config } = useAuth();
  const [r1, setR1] = useState(null);
  const [r2, setR2] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const [a, b] = await Promise.all([api.get("/submissions/r1"), api.get("/submissions/r2")]);
        setR1(a.data); setR2(b.data);
      } catch (e) { console.error("Failed to load submissions:", e); }
    })();
  }, []);

  const cs = config?.status; // upcoming, r1_open, r1_review, r2_open, judging, completed
  let activeRound = null;
  if (cs === "r1_open") activeRound = 1;
  else if (cs === "r2_open") activeRound = 2;

  // Decide what to show
  let title = "Submissions"; let body = null; let cta = null; let badgeClass = "badge-upcoming"; let badgeText = "not started"; let reason = null;

  if (activeRound === 1) {
    if (r1?.eligible) {
      if (r1.submission?.status === "submitted") {
        title = "Round 1 · submitted";
        badgeClass = "badge-accepted"; badgeText = "submitted";
        cta = { to: "/submit/round1", label: "View Submission" };
      } else if (r1.submission?.status === "draft") {
        title = "Round 1 · draft saved";
        badgeClass = "badge-invited"; badgeText = "draft saved";
        cta = isLeader ? { to: "/submit/round1", label: "Continue Draft" } : null;
      } else {
        title = "Round 1 is open";
        cta = isLeader ? { to: "/submit/round1", label: "Start Submission" } : null;
        body = "Submit your idea write-up and pitch video.";
      }
      if (!isLeader && !r1.submission) body = "Only the team leader can submit on behalf of the team.";
    } else {
      title = "Round 1";
      reason = r1?.reason || "Not eligible";
    }
  } else if (activeRound === 2) {
    if (r2?.eligible) {
      if (r2.submission?.status === "submitted") {
        title = "Round 2 · submitted";
        badgeClass = "badge-accepted"; badgeText = "submitted";
        cta = { to: "/submit/round2", label: "View Submission" };
      } else if (r2.submission?.status === "draft") {
        title = "Round 2 · draft saved";
        badgeClass = "badge-invited"; badgeText = "draft saved";
        cta = isLeader ? { to: "/submit/round2", label: "Continue Draft" } : null;
      } else {
        title = "Round 2 is open";
        cta = isLeader ? { to: "/submit/round2", label: "Start Submission" } : null;
        body = "Submit your prototype, repo link, and demo video.";
      }
    } else {
      title = "Round 2";
      reason = r2?.reason || (teamStatus !== "shortlisted" ? "Only shortlisted teams can submit Round 2." : "Not eligible");
    }
  } else {
    title = "Submissions";
    if (cs === "upcoming") reason = "Submission rounds have not opened yet.";
    else if (cs === "r1_review") reason = "Round 1 is closed — judges are reviewing submissions.";
    else if (cs === "judging") reason = "Judges are scoring Round 2 submissions.";
    else if (cs === "completed") reason = "IdeaThon is complete. See the results page.";
    else reason = "Submissions not currently open.";
  }

  return (
    <div className="mt-6 card-surface border-l-2 border-brand" data-testid="submission-status-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-brand" strokeWidth={1.5} />
            <div className="label-eyebrow">Submissions</div>
            <span className={`badge ${badgeClass}`}>{badgeText}</span>
          </div>
          <div className="font-display text-2xl text-white mt-2">{title}</div>
          {body && <p className="text-sm text-gray-400 mt-2 max-w-xl">{body}</p>}
          {reason && <p className="text-sm text-gray-500 mt-2 max-w-xl">{reason}</p>}
          {teamStatus === "shortlisted" && (
            <p className="text-xs text-brand/80 mt-2 max-w-xl" data-testid="shortlist-note">
              You're shortlisted for Round 2 — judges have not finished reviewing Round 2 yet. Finalists are announced after judging.
            </p>
          )}
          {teamStatus === "finalist" && (
            <p className="text-xs text-[#FBBF24] mt-2 max-w-xl" data-testid="finalist-note">
              Your team is a Finalist! Winners will be announced soon.
            </p>
          )}
        </div>
        {cta && (
          <Link to={cta.to} className="btn-primary flex items-center gap-2 self-center" data-testid="submission-cta">
            {cta.label} <ArrowRight className="w-4 h-4" />
          </Link>
        )}
      </div>

      {/* Always-available read-only access to past submissions */}
      {(r1?.submission?.status === "submitted" || r2?.submission?.status === "submitted") && (
        <div className="mt-5 pt-5 border-t border-white/5" data-testid="view-submissions">
          <div className="label-eyebrow mb-2">Your submitted entries</div>
          <div className="flex flex-wrap gap-2">
            {r1?.submission?.status === "submitted" && (
              <Link to="/submit/round1" className="btn-secondary !py-2 !px-3 text-xs flex items-center gap-2" data-testid="view-r1-submission">
                <FileText className="w-3.5 h-3.5" /> View Round 1 submission
              </Link>
            )}
            {r2?.submission?.status === "submitted" && (
              <Link to="/submit/round2" className="btn-secondary !py-2 !px-3 text-xs flex items-center gap-2" data-testid="view-r2-submission">
                <FileText className="w-3.5 h-3.5" /> View Round 2 submission
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const CERT_PRIORITY = { winner: 3, finalist: 2, participation: 1 };

function ResultBanner({ team }) {
  const { config } = useAuth();
  const [state, setState] = useState(null); // { certificates, released, certificate_release_at }

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/certificates/mine");
        setState(data);
      } catch { /* no certs / not announced */ }
    })();
  }, []);

  if (!state || !Array.isArray(state.certificates) || state.certificates.length === 0) return null;

  // Pick the certificate for THIS team (fallback: highest-priority cert overall)
  const forTeam = state.certificates.filter((c) => c.team_name === team.name);
  const pool = forTeam.length ? forTeam : state.certificates;
  const cert = [...pool].sort((a, b) => (CERT_PRIORITY[b.cert_type] || 0) - (CERT_PRIORITY[a.cert_type] || 0))[0];
  if (!cert) return null;

  const released = !!state.released;
  const relDate = state.certificate_release_at;
  const slug = config?.results_slug;
  const resultsHref = slug ? `/results/${slug}` : "/results";

  const themes = {
    winner: {
      wrap: "border-[#FBBF24]/50 bg-gradient-to-r from-[#FBBF24]/10 to-[#FF6B2B]/5",
      icon: <Trophy className="w-7 h-7 text-[#FBBF24]" strokeWidth={1.5} />,
      eyebrow: "Champion",
      title: `Rank ${cert.rank} — ${cert.edition_label || "IdeaThon 2026"}`,
      sub: cert.prize_amount ? `Congratulations! Your team won ₹${Number(cert.prize_amount).toLocaleString("en-IN")}.` : "Congratulations on your win!",
      accent: "text-[#FBBF24]",
    },
    finalist: {
      wrap: "border-[#FBBF24]/40 bg-[#FBBF24]/[0.06]",
      icon: <Medal className="w-7 h-7 text-[#FBBF24]" strokeWidth={1.5} />,
      eyebrow: "Finalist",
      title: `Round 2 Finalist — ${cert.edition_label || "IdeaThon 2026"}`,
      sub: "Your team made it to the final round. Well done!",
      accent: "text-[#FBBF24]",
    },
    participation: {
      wrap: "border-brand/40 bg-brand/[0.06]",
      icon: <Award className="w-7 h-7 text-brand" strokeWidth={1.5} />,
      eyebrow: "Participant",
      title: `${cert.edition_label || "IdeaThon 2026"} Participant`,
      sub: "Thanks for taking part. Your participation certificate is ready.",
      accent: "text-brand",
    },
  };
  const t = themes[cert.cert_type] || themes.participation;
  const downloadUrl = `${API_BASE}/certificates/${cert.verification_id}/download`;
  const certUrl = `${window.location.origin}/verify-certificate/${cert.verification_id}`;
  const certName = `${cert.label || "Certificate"} — ${cert.edition_label || "IdeaThon 2026"}`;
  const issued = cert.issued_date ? new Date(cert.issued_date) : new Date();
  const issueYear = isNaN(issued) ? new Date().getFullYear() : issued.getFullYear();
  const issueMonth = isNaN(issued) ? new Date().getMonth() + 1 : issued.getMonth() + 1;
  // LinkedIn "Add to profile" — drops the credential straight into Licenses & Certifications
  const addToLinkedInUrl = "https://www.linkedin.com/profile/add?" + new URLSearchParams({
    startTask: "CERTIFICATION_NAME",
    name: certName,
    organizationName: "NoobSync Innovations",
    issueYear: String(issueYear),
    issueMonth: String(issueMonth),
    certUrl,
    certId: cert.verification_id,
  }).toString();

  return (
    <div className={`mt-6 p-5 sm:p-6 border ${t.wrap}`} data-testid="result-banner" data-cert-type={cert.cert_type}>
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center border border-white/10 bg-black/20">{t.icon}</div>
        <div className="flex-1 min-w-[220px]">
          <div className={`label-eyebrow ${t.accent}`} data-testid="result-eyebrow">{t.eyebrow}</div>
          <div className="font-display text-2xl sm:text-3xl text-white mt-1" data-testid="result-title">{t.title}</div>
          <p className="text-sm text-gray-300 mt-2 max-w-xl">{t.sub}</p>
          {cert.project_title && cert.project_title !== "—" && (
            <p className="text-xs text-gray-500 mt-1">Project: <span className="text-gray-400">{cert.project_title}</span></p>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-3">
            {released ? (
              <>
                <a href={downloadUrl} target="_blank" rel="noopener noreferrer"
                   className="btn-primary flex items-center gap-2 !py-2.5" data-testid="cert-download-btn">
                  <Download className="w-4 h-4" strokeWidth={1.5} /> Download Certificate
                </a>
                <a href={addToLinkedInUrl} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-2 px-4 py-2.5 text-sm border border-[#0A66C2]/50 bg-[#0A66C2]/10 text-[#5BA4F0] hover:bg-[#0A66C2]/20 transition-colors"
                   data-testid="cert-add-linkedin-btn">
                  <Linkedin className="w-4 h-4" strokeWidth={1.5} /> Add to LinkedIn
                </a>
              </>
            ) : (
              <span className="inline-flex items-center gap-2 px-4 py-2.5 border border-white/10 bg-white/5 text-gray-500 text-sm cursor-not-allowed"
                    data-testid="cert-locked-btn" title="Certificate not yet released">
                <Lock className="w-4 h-4" strokeWidth={1.5} />
                Certificate available on {formatDay(relDate)}
              </span>
            )}
            <Link to={resultsHref} className="inline-flex items-center gap-1.5 text-sm text-brand hover:underline label-eyebrow" data-testid="view-results-link">
              View Results <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {cert.cert_type === "winner" && <ShareWin cert={cert} team={team} resultsHref={resultsHref} />}
        </div>
      </div>
    </div>
  );
}

function ShareWin({ cert, team, resultsHref }) {
  const resultsUrl = `${window.location.origin}${resultsHref}`;
  const edition = cert.edition_label || "IdeaThon 2026";
  const project = cert.project_title && cert.project_title !== "—" ? `"${cert.project_title}"` : "our project";
  const teamName = cert.team_name || team.name;
  const hashtags = "IdeaThon2026,NoobSync,Hackathon";

  // X / Twitter — concise (URL auto-shortened by t.co; hashtags via param)
  const tweet = `🏆 Thrilled to share that Team ${teamName} won Rank ${cert.rank} at ${edition} with ${project}! Grateful to NoobSync Innovate. 🚀`;
  const xUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweet)}&url=${encodeURIComponent(resultsUrl)}&hashtags=${encodeURIComponent(hashtags)}`;

  // LinkedIn — composer pre-filled with full post text (incl. link + hashtags)
  const liText = `🏆 Proud to announce that Team ${teamName} secured Rank ${cert.rank} at ${edition} with ${project}!\n\nA huge thank you to NoobSync Innovate and my incredible teammates for this journey.\n\nSee the full results: ${resultsUrl}\n\n#IdeaThon2026 #NoobSync #Hackathon`;
  const liUrl = `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(liText)}`;

  return (
    <div className="mt-5 pt-4 border-t border-white/10" data-testid="share-win">
      <div className="label-eyebrow text-gray-400 mb-2">Share your win</div>
      <div className="flex flex-wrap gap-2">
        <a href={liUrl} target="_blank" rel="noopener noreferrer"
           className="inline-flex items-center gap-2 px-3 py-2 text-xs border border-[#0A66C2]/50 bg-[#0A66C2]/10 text-[#5BA4F0] hover:bg-[#0A66C2]/20 transition-colors"
           data-testid="share-linkedin-btn">
          <Linkedin className="w-4 h-4" strokeWidth={1.5} /> Share on LinkedIn
        </a>
        <a href={xUrl} target="_blank" rel="noopener noreferrer"
           className="inline-flex items-center gap-2 px-3 py-2 text-xs border border-white/20 bg-white/5 text-gray-200 hover:bg-white/10 transition-colors"
           data-testid="share-x-btn">
          <Twitter className="w-4 h-4" strokeWidth={1.5} /> Share on X
        </a>
      </div>
    </div>
  );
}
